import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, ExternalLink, Flag, User } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import {
  adminReportsPath,
  dismissAdminReportPath,
  resolveAdminReportPath,
  reviewAdminReportPath,
} from "@/routes"

interface ReportedContent {
  type: string
  title: string
  excerpt: string | null
  url: string
  author: { id: number; name: string } | null
}

interface Report {
  id: number
  status: "pending" | "reviewed" | "resolved" | "dismissed"
  reason: string
  description: string | null
  moderator_notes: string | null
  action_taken: string | null
  reviewed_at: string | null
  created_at: string
  reporter: { id: number; name: string }
  reporter_reports_count: number
  moderator: { id: number; name: string } | null
  reported_content: ReportedContent | null
}

interface Props {
  report: Report
}

export default function AdminReportShow({ report }: Props) {
  const [moderatorNotes, setModeratorNotes] = useState(
    report.moderator_notes ?? "",
  )
  const [actionTaken, setActionTaken] = useState(report.action_taken ?? "")
  const [processing, setProcessing] = useState(false)

  const canModerate =
    report.status === "pending" || report.status === "reviewed"

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const statusColors: Record<string, string> = {
    pending:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400",
    reviewed:
      "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400",
    resolved:
      "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400",
    dismissed:
      "border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400",
  }

  const statusLabel: Record<string, string> = {
    pending: "Pendiente",
    reviewed: "En revisión",
    resolved: "Resuelto",
    dismissed: "Descartado",
  }

  const reasonLabel: Record<string, string> = {
    spam: "Spam",
    inappropriate: "Inapropiado",
    harassment: "Acoso",
    fake: "Falso",
    scam: "Estafa",
    dangerous: "Peligroso",
    other: "Otro",
  }

  const contentTypeLabel: Record<string, string> = {
    ForumPost: "Post del foro",
    ForumAnswer: "Respuesta del foro",
    User: "Usuario",
    Event: "Evento",
    EventComment: "Comentario de evento",
    Announcement: "Anuncio",
    Interview: "Entrevista",
    Story: "Historia",
    Business: "Negocio",
    Product: "Producto",
    Review: "Reseña",
    Raffle: "Sorteo",
    ChatMessage: "Mensaje de chat",
  }

  const actionLabel: Record<string, string> = {
    advertencia_enviada: "Advertencia enviada al usuario",
    contenido_eliminado: "Contenido eliminado",
    usuario_suspendido: "Usuario suspendido",
  }

  const handleAction = (path: string) => {
    setProcessing(true)
    router.patch(
      path,
      { moderator_notes: moderatorNotes, action_taken: actionTaken },
      { onFinish: () => setProcessing(false) },
    )
  }

  return (
    <AppLayout>
      <Head title={`Reporte #${report.id} - Panel Admin`} />

      <div className="mx-auto max-w-5xl space-y-6 p-4">
        <Link
          href={adminReportsPath()}
          className="text-muted-foreground hover:text-foreground flex items-center text-sm"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Volver a reportes
        </Link>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Información del reporte */}
          <div className="bg-card rounded-lg">
            <div className="border-b px-6 py-4">
              <h2 className="flex items-center text-lg font-semibold">
                <Flag className="mr-2 h-5 w-5" />
                Reporte #{report.id}
              </h2>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColors[report.status]}`}
                >
                  {statusLabel[report.status]}
                </span>
              </div>

              <div>
                <p className="text-muted-foreground text-xs tracking-wide uppercase">
                  Razón
                </p>
                <p className="font-medium">
                  {reasonLabel[report.reason] ?? report.reason}
                </p>
              </div>

              {report.description && (
                <div>
                  <p className="text-muted-foreground text-xs tracking-wide uppercase">
                    Descripción del usuario
                  </p>
                  <p className="text-sm">{report.description}</p>
                </div>
              )}

              <div>
                <p className="text-muted-foreground text-xs tracking-wide uppercase">
                  Reportado por
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <User className="text-muted-foreground h-4 w-4" />
                  <span className="text-sm font-medium">
                    {report.reporter.name}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    ({report.reporter_reports_count} reportes en total)
                  </span>
                </div>
              </div>

              <div>
                <p className="text-muted-foreground text-xs tracking-wide uppercase">
                  Fecha
                </p>
                <p className="text-sm">{formatDate(report.created_at)}</p>
              </div>

              {report.reviewed_at && (
                <div>
                  <p className="text-muted-foreground text-xs tracking-wide uppercase">
                    En revisión desde
                  </p>
                  <p className="text-sm">{formatDate(report.reviewed_at)}</p>
                  {report.moderator && (
                    <p className="text-muted-foreground text-xs">
                      por {report.moderator.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contenido reportado */}
          <div className="bg-card rounded-lg">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Contenido reportado</h2>
            </div>
            <div className="space-y-3 px-6 py-4">
              {report.reported_content ? (
                <>
                  <div>
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Tipo
                    </p>
                    <p className="text-sm font-medium">
                      {contentTypeLabel[report.reported_content.type] ??
                        report.reported_content.type}
                    </p>
                  </div>

                  <div>
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Título
                    </p>
                    <p className="text-sm font-medium">
                      {report.reported_content.title}
                    </p>
                  </div>

                  {report.reported_content.excerpt && (
                    <div>
                      <p className="text-muted-foreground text-xs tracking-wide uppercase">
                        Extracto
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {report.reported_content.excerpt}
                      </p>
                    </div>
                  )}

                  {report.reported_content.author && (
                    <div>
                      <p className="text-muted-foreground text-xs tracking-wide uppercase">
                        Autor
                      </p>
                      <p className="text-sm">
                        {report.reported_content.author.name}
                      </p>
                    </div>
                  )}

                  {report.reported_content.url !== "#" && (
                    <a
                      href={report.reported_content.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary flex items-center gap-1 text-sm hover:underline"
                    >
                      Ver contenido
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Contenido no disponible (puede haber sido eliminado)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Panel de acciones */}
        {canModerate && (
          <div className="bg-card rounded-lg">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Acciones de moderación</h2>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Notas del moderador
                </label>
                <Textarea
                  value={moderatorNotes}
                  onChange={(e) => setModeratorNotes(e.target.value)}
                  placeholder="Agregar notas sobre la decisión..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Acción tomada</label>
                <select
                  value={actionTaken}
                  onChange={(e) => setActionTaken(e.target.value)}
                  className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="">— Seleccionar acción —</option>
                  <option value="advertencia_enviada">
                    Advertencia enviada al usuario
                  </option>
                  <option value="contenido_eliminado">
                    Contenido eliminado
                  </option>
                  <option value="usuario_suspendido">Usuario suspendido</option>
                </select>
                {!actionTaken && (
                  <p className="text-muted-foreground text-xs">
                    Seleccioná una acción para resolver. Si el reporte no merece
                    acción, usá <strong>Descartar</strong>.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                {report.status === "pending" && (
                  <Button
                    variant="outline"
                    disabled={processing}
                    onClick={() =>
                      handleAction(reviewAdminReportPath(report.id))
                    }
                  >
                    Marcar como en revisión
                  </Button>
                )}
                <Button
                  disabled={processing || !actionTaken}
                  onClick={() =>
                    handleAction(resolveAdminReportPath(report.id))
                  }
                >
                  Resolver
                </Button>
                <Button
                  variant="destructive"
                  disabled={processing}
                  onClick={() =>
                    handleAction(dismissAdminReportPath(report.id))
                  }
                >
                  Descartar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Vista read-only si ya fue procesado */}
        {!canModerate && (report.moderator_notes ?? report.action_taken) && (
          <div className="bg-card rounded-lg">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Decisión de moderación</h2>
            </div>
            <div className="space-y-2 px-6 py-4">
              {report.moderator_notes && (
                <div>
                  <p className="text-muted-foreground text-xs tracking-wide uppercase">
                    Notas del moderador
                  </p>
                  <p className="text-sm">{report.moderator_notes}</p>
                </div>
              )}
              {report.action_taken && (
                <div>
                  <p className="text-muted-foreground text-xs tracking-wide uppercase">
                    Acción tomada
                  </p>
                  <p className="text-sm">
                    {actionLabel[report.action_taken] ?? report.action_taken}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
